import { useEffect, useMemo, useState } from "react";
import { collection, onSnapshot, query, orderBy } from "firebase/firestore";
import { db, firebaseListo } from "./firebase";
import {
  COLECCION_CATALOGO,
  EMPRESA_ID,
  aProductoDeTienda,
  dbZentria,
} from "./zentria";
// Solo los SERVICIOS conservan respaldo: son contenido de la empresa, no
// inventario. Los productos salen de CGO o no salen. CATEGORIAS_DEMO queda
// como fuente de nombres bonitos para las familias que coincidan.
import { CATEGORIAS_DEMO, SERVICIOS_DEMO } from "./catalogoDemo";

/**
 * Lee una coleccion de Firestore y, si no hay backend configurado o la
 * coleccion esta vacia, devuelve los datos de demostracion. `esDemo` permite
 * que la interfaz avise cuando lo que se ve no es informacion real.
 */
function useColeccionConRespaldo(ruta, respaldo, campoOrden = "orden") {
  const [items, setItems] = useState(respaldo);
  const [esDemo, setEsDemo] = useState(true);
  const [loading, setLoading] = useState(firebaseListo);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!firebaseListo) {
      setItems(respaldo);
      setEsDemo(true);
      setLoading(false);
      return;
    }

    const q = query(collection(db, ruta), orderBy(campoOrden));
    const unsub = onSnapshot(
      q,
      (snap) => {
        if (snap.empty) {
          setItems(respaldo);
          setEsDemo(true);
        } else {
          const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
          setItems(docs);
          // Aunque los datos vengan de Firestore, si algun registro conserva la
          // marca `demo` es que se sembro el catalogo de ejemplo y sus precios
          // siguen siendo referenciales: el aviso tiene que seguir a la vista.
          setEsDemo(docs.some((d) => d.demo === true));
        }
        setLoading(false);
      },
      (err) => {
        // Sin permisos o sin indice: no dejamos la web en blanco.
        console.warn(`[Aceros Lima] No se pudo leer ${ruta}:`, err.message);
        setItems(respaldo);
        setEsDemo(true);
        setError(err);
        setLoading(false);
      }
    );

    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ruta, campoOrden]);

  return { items, esDemo, loading, error };
}

/**
 * El catalogo sale del INVENTARIO que la empresa lleva en CGO, no de esta web.
 *
 * Se lee `tenants/{empresa}/cgo_catalogo_publico` del proyecto de Zentria: una
 * proyeccion recortada del inventario con lo que una tienda necesita enseñar.
 * El inventario de verdad —costo de compra, stock exacto, stock minimo— se
 * queda cerrado en Zentria y no llega nunca al navegador.
 *
 * Un articulo esta en la tienda cuando tiene precio de venta cargado en CGO.
 * Asi el insumo interno y el retazo no aparecen sin que nadie tenga que
 * acordarse de marcar nada, y quitar el precio retira el articulo.
 *
 * Sigue en pie el respaldo de demostracion: si no hay inventario publicado
 * todavia, la web se ve completa y avisa de que los precios son de ejemplo.
 */
export function useProductos() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const ref = collection(dbZentria, "tenants", EMPRESA_ID, COLECCION_CATALOGO);

    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (snap.empty) {
          // Sin inventario publicado no hay catalogo. Antes se rellenaba con
          // productos de ejemplo y el cliente veia una tienda que no existe,
          // con precios que no son de la empresa.
          setItems([]);
        } else {
          const docs = snap.docs.map((d) => aProductoDeTienda(d.id, d.data()));
          // CGO no ordena el catalogo: en una tienda de acero el orden util es
          // por familia y luego por nombre.
          docs.sort(
            (a, b) =>
              a.categoria.localeCompare(b.categoria) ||
              a.nombre.localeCompare(b.nombre, "es", { numeric: true })
          );
          setItems(docs);
        }
        setLoading(false);
      },
      (err) => {
        // Un fallo de lectura tampoco justifica inventarse un catalogo: es
        // preferible una tienda vacia que una con precios que no son suyos.
        console.warn("[Aceros Lima] No se pudo leer el inventario:", err.message);
        setItems([]);
        setError(err);
        setLoading(false);
      }
    );

    return unsub;
  }, []);

  const productos = useMemo(
    () => items.filter((p) => p.activo !== false),
    [items]
  );
  return { productos, todos: items, loading, error };
}

/**
 * Las familias salen del propio catalogo: el inventario no tiene una coleccion
 * de categorias, solo un campo por articulo. Se deducen de lo que hay, y si el
 * identificador coincide con una familia conocida se le presta su nombre y su
 * resumen para que la portada siga leyendose bien.
 */
export function useCategorias() {
  const { productos, loading } = useProductos();

  const categorias = useMemo(() => {
    const conocidas = new Map(CATEGORIAS_DEMO.map((c) => [c.slug, c]));
    const vistas = new Map();

    for (const p of productos) {
      const slug = (p.categoria || "").trim();
      if (!slug || vistas.has(slug)) continue;
      const conocida = conocidas.get(slug);
      vistas.set(slug, {
        id: slug,
        slug,
        // El nombre que puso el cliente en su inventario manda; el catalogo de
        // ejemplo solo presta el suyo cuando la familia coincide.
        nombre:
          p.categoriaEtiqueta ||
          conocida?.nombre ||
          slug.charAt(0).toUpperCase() + slug.slice(1),
        resumen: conocida?.resumen || "",
        orden: conocida?.orden ?? 99,
      });
    }

    return [...vistas.values()].sort(
      (a, b) => a.orden - b.orden || a.nombre.localeCompare(b.nombre)
    );
  }, [productos]);

  return { categorias, loading };
}

export function useServicios() {
  const { items, esDemo, loading } = useColeccionConRespaldo(
    "servicios",
    SERVICIOS_DEMO
  );
  return { servicios: items, esDemo, loading };
}

/** Un producto por id, resuelto desde la misma fuente que el listado. */
export function useProducto(id) {
  const { productos, loading } = useProductos();
  const producto = useMemo(
    () => productos.find((p) => p.id === id) || null,
    [productos, id]
  );
  return { producto, loading };
}

/**
 * Filtrado y ordenamiento del catalogo. Se hace en cliente: el catalogo de una
 * distribuidora rara vez pasa de unos cientos de SKU y asi la busqueda es
 * instantanea y sin indices compuestos.
 */
export function filtrarProductos(productos, { texto, categoria, orden } = {}) {
  let out = [...productos];

  if (categoria && categoria !== "todos") {
    out = out.filter((p) => p.categoria === categoria);
  }

  if (texto && texto.trim()) {
    const t = texto.trim().toLowerCase();
    out = out.filter((p) => {
      const specs = Object.values(p.especificaciones || {}).join(" ");
      return `${p.nombre} ${p.sku} ${p.descripcion || ""} ${specs}`
        .toLowerCase()
        .includes(t);
    });
  }

  switch (orden) {
    case "precio-asc":
      out.sort((a, b) => (a.precio ?? Infinity) - (b.precio ?? Infinity));
      break;
    case "precio-desc":
      out.sort((a, b) => (b.precio ?? -Infinity) - (a.precio ?? -Infinity));
      break;
    case "nombre":
      out.sort((a, b) => a.nombre.localeCompare(b.nombre, "es"));
      break;
    default:
      out.sort(
        (a, b) =>
          Number(b.destacado || false) - Number(a.destacado || false) ||
          (a.orden ?? 99) - (b.orden ?? 99)
      );
  }

  return out;
}
